// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { assoc, compose } from 'lodash/fp';
import {
  activateSubscription,
  adjustSubscriptionInfo,
  cancelSubscription,
  clearSubscriptionPlanId,
  getAllSubscriptions,
  getBillingInvoices,
  getCurrentUserSubscription,
  getSubscriptionPlanId,
  hasAdblocker,
  setSubscription,
  startTrial,
  switchSubscription,
} from 'store/actionCreators/subscriptions';
import {
  OnboardingCatalogItem,
  SubscriptionPlan,
  UserSubscription,
} from 'types';
import { BillingInvoice } from 'types/billingInvoice';

interface SubscriptionsState {
  allSubscriptions: SubscriptionPlan[] | null;
  allOnboardingPlans: OnboardingCatalogItem[];
  allSubscriptionsFetchedAt: number | null;
  allSubscriptionsGeoKey: string | null;
  currentUserSubscription: UserSubscription;
  userSelectedSubscription: { id: string; frequency: string };
  paypalSubscriptionPlanId: string | null;
  billingInvoices: BillingInvoice[];
  billingInvoicesLoaded: boolean;
  isBillingInvoicesLoading: boolean;
  billingInvoicesError: unknown | null;
  isLoading: boolean;
  isRefreshingSubscription: boolean;
  currentUserSubscriptionLoaded: boolean;
  isPlansInitialLoading: boolean;
  isPlansRefreshing: boolean;
  plansError: unknown | null;
  hasAdblocker: boolean;
  trialStartErrorKey: string | null;
  isStartingTrial: boolean;
}

const initialState: SubscriptionsState = {
  allSubscriptions: null,
  allOnboardingPlans: [],
  allSubscriptionsFetchedAt: null,
  allSubscriptionsGeoKey: null,
  currentUserSubscription: null,
  userSelectedSubscription: null,
  paypalSubscriptionPlanId: null,
  billingInvoices: [],
  billingInvoicesLoaded: false,
  isBillingInvoicesLoading: false,
  billingInvoicesError: null,
  isLoading: false,
  isRefreshingSubscription: false,
  currentUserSubscriptionLoaded: false,
  isPlansInitialLoading: false,
  isPlansRefreshing: false,
  plansError: null,
  hasAdblocker: false,
  trialStartErrorKey: null,
  isStartingTrial: false,
};

export default (
  state: SubscriptionsState = initialState,
  { type, payload },
): SubscriptionsState => {
  switch (type) {
    case getAllSubscriptions.TRIGGER: {
      const isSilent = Boolean(payload?.silent);
      const hasPlansCache = Boolean(state.allSubscriptions?.length);
      return compose(
        assoc(['plansError'], null),
        assoc(['isPlansInitialLoading'], !hasPlansCache && !isSilent),
        assoc(['isPlansRefreshing'], hasPlansCache || isSilent),
      )(state);
    }
    case getAllSubscriptions.SUCCESS:
      return compose(
        assoc(['allSubscriptions'], payload?.data || []),
        assoc(['allOnboardingPlans'], payload?.onboarding || []),
        assoc(['allSubscriptionsFetchedAt'], payload?.fetchedAt || Date.now()),
        assoc(['allSu bscriptionsGeoKey'], payload?.geoKey || null),
        assoc(['plansError'], null),
        assoc(['isPlansInitialLoading'], false),
        assoc(['isPlansRefreshing'], false),
      )(state);
    case getAllSubscriptions.FAILURE:
      return compose(
        assoc(['allSubscriptions'], state.allSubscriptions),
        assoc(['plansError'], payload || null),
        assoc(['isPlansInitialLoading'], false),
        assoc(['isPlansRefreshing'], false),
      )(state);
    case getSubscriptionPlanId.TRIGGER:
      return compose(assoc(['isLoading'], true))(state);
    case getSubscriptionPlanId.SUCCESS:
      return compose(
        assoc(['paypalSubscriptionPlanId'], payload),
        assoc(['isLoading'], false),
      )(state);
    case getSubscriptionPlanId.FAILURE:
      return compose(
        assoc(['paypalSubscriptionPlanId'], null),
        assoc(['isLoading'], false),
      )(state);
    case clearSubscriptionPlanId.TRIGGER:
      return compose(assoc(['paypalSubscriptionPlanId'], null))(state);
    case getCurrentUserSubscription.TRIGGER:
      return compose(
        assoc(
          ['isLoading'],
          state.currentUserSubscriptionLoaded ? state.isLoading : true,
        ),
        assoc(
          ['isRefreshingSubscription'],
          state.currentUserSubscriptionLoaded,
        ),
      )(state);
    case getBillingInvoices.TRIGGER:
      return compose(
        assoc(['isBillingInvoicesLoading'], true),
        assoc(['billingInvoicesError'], null),
      )(state);
    case getBillingInvoices.SUCCESS:
      return compose(
        assoc(['billingInvoices'], payload || []),
        assoc(['billingInvoicesLoaded'], true),
        assoc(['isBillingInvoicesLoading'], false),
        assoc(['billingInvoicesError'], null),
      )(state);
    case getBillingInvoices.FAILURE:
      return compose(
        assoc(['isBillingInvoicesLoading'], false),
        assoc(['billingInvoicesLoaded'], true),
        assoc(['billingInvoicesError'], payload || null),
      )(state);
    case getCurrentUserSubscription.FAILURE:
      return compose(
        assoc(
          ['currentUserSubscription'],
          state.currentUserSubscription ?? null,
        ),
        assoc(['isLoading'], false),
        assoc(['isRefreshingSubscription'], false),
        assoc(['currentUserSubscriptionLoaded'], true),
      )(state);
    case setSubscription.TRIGGER:
      return compose(assoc(['isLoading'], true))(state);
    case setSubscription.SUCCESS:
      return compose(assoc(['isLoading'], false))(state);
    case setSubscription.FAILURE:
    case cancelSubscription.FAILURE:
      return compose(assoc(['isLoading'], false))(state);
    case switchSubscription.TRIGGER: {
      return compose(assoc(['userSelectedSubscription'], payload))(state);
    }
    case startTrial.TRIGGER: {
      return compose(
        assoc(['isLoading'], true),
        assoc(['isStartingTrial'], true),
        assoc(['trialStartErrorKey'], null),
      )(state);
    }
    case startTrial.FAILURE:
      return compose(
        assoc(['isLoading'], false),
        assoc(['isStartingTrial'], false),
        assoc(['trialStartErrorKey'], payload || null),
      )(state);
    case activateSubscription.TRIGGER: {
      return compose(assoc(['isLoading'], true))(state);
    }
    case activateSubscription.SUCCESS:
    case getCurrentUserSubscription.SUCCESS:
    case cancelSubscription.SUCCESS:
      return compose(
        assoc(['currentUserSubscription'], payload),
        assoc(['isLoading'], false),
        assoc(['isRefreshingSubscription'], false),
        assoc(['currentUserSubscriptionLoaded'], true),
        assoc(['trialStartErrorKey'], null),
        assoc(['isStartingTrial'], false),
      )(state);
    case activateSubscription.FAILURE:
      return compose(
        assoc(['isLoading'], false),
        assoc(['isStartingTrial'], false),
      )(state);
    case cancelSubscription.TRIGGER: {
      return compose(assoc(['isLoading'], true))(state);
    }
    case hasAdblocker.SUCCESS:
      return compose(assoc(['hasAdblocker'], true))(state);
    case adjustSubscriptionInfo.TRIGGER: {
      const sub = state.currentUserSubscription;
      if (!sub?.info) return state;
      return assoc('currentUserSubscription', {
        ...sub,
        info: { ...sub.info, ...payload },
      })(state);
    }
    default:
      return state;
  }
};
